import { useState, useEffect } from "react";
import { Flex, Box, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { useQuery } from "@apollo/client";
import { HiArchive, HiCheck, HiFilm } from "react-icons/hi";

import { IoLockClosed } from "react-icons/io5";
import { FaDiscord, FaCommentDots } from "react-icons/fa";
import { SiFaceit } from "react-icons/si";
import { Link } from "react-router-dom";

import { analytics } from "MVVM/Analytics";
import { useUser } from "MVVM/Hooks/useUser";
import { useLinkedAccounts } from "MVVM/Hooks/useLinkedAccounts";
import { useTrialEligibility } from "MVVM/Hooks/useTrialEligibility";
import { useUnlimitedOnlyPaywall } from "MVVM/Hooks/useUnlimitedOnlyPaywall";
import { useResolvedPaymentProvider } from "MVVM/Hooks/useResolvedPaymentProvider";
import { StripePaystationState, xsollaModalOpen } from "MVVM/State";
import { StorageStatusAtom } from "MVVM/State/storagePolicy";
import { PromoSession } from "MVVM/State/promoSession";
import { CLIP_STORAGE_STATUS } from "MVVM/GraphQL/savedClipStatus";
import { EPriceTabView } from "MVVM/@types";
import { TTierKey } from "MVVM/Views/Pricing/Components/PricingCards/types";

import { ReactComponent as EssentialsIconSVG } from "MVVM/Assets/Avatar/Icons/essentials.svg";
import { ReactComponent as ProIconSVG } from "MVVM/Assets/Avatar/Icons/pro.svg";
import { ReactComponent as PlatinumIconSVG } from "MVVM/Assets/Avatar/Icons/platinum.svg";
import {
  TIER_PRICING,
  TIER_STRIPE_LOOKUP,
  TIER_XSOLLA_LOOKUP,
  TIER_NAMES,
  STORAGE_ADDON_OPTIONS,
  IStorageAddOn,
  FACEIT_PREMIUM_COUPON,
  FACEIT_PREMIUM_DISCOUNT_PERCENT,
  getTierAccentColor,
  getTierButtonGradient,
  getTierHeaderGradient,
  getTierBorderColor,
  getTierGlowColor,
} from "MVVM/Utilities/SubscriptionTiers";

const UPGRADE_TIERS = ["ESSENTIALS", "PRO", "CREATOR_PLAT"] as const;

// Maps the card's tier key to the promo document's per-tier pricing key — same
// mapping the upgrade page (PricingCards) uses to honor live promo pricing.
const TIER_PROMO_KEY: Record<string, "essentials" | "pro" | "platinum" | null> =
  {
    FREE: null,
    ESSENTIALS: "essentials",
    PRO: "pro",
    CREATOR_PLAT: "platinum",
  };

const TIER_STORAGE: Record<string, string> = {
  ESSENTIALS: "50 clips",
  PRO: "600 clips",
  CREATOR_PLAT: "Unlimited",
};

const TIER_STORAGE_NUM: Record<string, number | null> = {
  FREE: 10,
  ESSENTIALS: 50,
  PRO: 600,
  CREATOR_PLAT: null,
};

const getTierIcon = (type: string) => {
  switch (type) {
    case "ESSENTIALS":
      return <EssentialsIconSVG />;
    case "PRO":
      return <ProIconSVG />;
    case "CREATOR_PLAT":
      return <PlatinumIconSVG />;
    default:
      return null;
  }
};

interface PaywallBannerData {
  featureName: string;
  upgradeAction: string;
  requiredTierKey: TTierKey;
  description?: string;
}

interface StorageAddonModalProps {
  onClose: () => void;
  analyticsSource: string;
  initialPlan?: string;
  initialBillingPeriod?: EPriceTabView;
  paywallBanner?: PaywallBannerData;
  // Shows a "add the Allstar Discord bot for more clips" CTA — used by the
  // clip-limit paywall, where the bot is a free way to raise the clip limit.
  showDiscordBotCta?: boolean;
  // !allstar CTA; CS2-only, so callers gate this on the clip's game.
  isCs2Clip?: boolean;
}

// Allstar Discord bot info page; adding the bot to a server raises clip limits.
const ALLSTAR_BOT_ROUTE = "/allstarbot";
const ALLSTAR_ROUTE = "/!allstar";
// "r, g, b" accent triplets for each CTA card's icon chip + arrow
const DISCORD_ACCENT = "88, 101, 242"; // Discord blurple (#5865f2)
const ALLSTAR_ACCENT = "77, 189, 148"; // envy green (#4dbd94)

const StorageAddonModal = ({
  onClose,
  analyticsSource,
  initialPlan,
  initialBillingPeriod,
  paywallBanner,
  showDiscordBotCta,
  isCs2Clip,
}: StorageAddonModalProps) => {
  const { allstarUser } = useUser();
  const { hasFaceitPremiumDiscount } = useLinkedAccounts();
  const setPaystation = useSetRecoilState(StripePaystationState);
  const setXsollaPaystation = useSetRecoilState(xsollaModalOpen);
  const setStorageStatus = useSetRecoilState(StorageStatusAtom);
  const storageStatus = useRecoilValue(StorageStatusAtom);

  const { provider, resolve: resolveProvider } = useResolvedPaymentProvider();
  const isXsolla = provider === "xsolla";

  // Live upgrade-page promo: when present, its per-tier sale price + discount
  // override the base pricing on each plan card (mirrors the upgrade page).
  const activePromo = useRecoilValue(PromoSession)?.activeUpgradePagePromo;

  useEffect(() => {
    resolveProvider();
  }, [resolveProvider]);

  const currentTier = allstarUser?.user?.userGroup?.type ?? "FREE";
  const isFreeUser = currentTier === "FREE";

  // Fetch storage status if not already in the atom (e.g. opened from upgrade page)
  useQuery(CLIP_STORAGE_STATUS, {
    skip: !!storageStatus,
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const status = data?.me?.clipStorageStatus;
      if (status?.success) {
        setStorageStatus({
          savedCount: status.savedCount ?? 0,
          protectedClipCount: status.protectedClipCount ?? 0,
          unsavedCount: status.unsavedCount ?? 0,
          limit: status.limit ?? null,
          isUnlimited: status.limit === null,
        });
      }
    },
  });

  // Whether the free trial can be offered to this user, ignoring the
  // currently-selected billing interval. Drives both the trial CTA gate and
  // the default billing period below.
  const { isTrialEligible: trialOfferable, requiresCard: trialRequiresCard } =
    useTrialEligibility();

  // Filter upgrade tiers to only those above current
  const tierOrder = ["ESSENTIALS", "PRO", "CREATOR_PLAT"] as const;
  const currentTierIndex = tierOrder.indexOf(
    currentTier as (typeof tierOrder)[number],
  );

  // Unlimited-only paywall (ALL-1931/ALL-1933): FREE users who never
  // subscribed see the Unlimited card alone on every source of this modal;
  // anyone who has paid keeps the full grid for tier switches.
  const { unlimitedOnly: unlimitedOnlyActive, variant: paywallVariant } =
    useUnlimitedOnlyPaywall();

  // The banner names the tier the user can actually buy here: when treatment
  // hides everything below Unlimited, "requires Basic" would point at a card
  // the grid doesn't show.
  const bannerTierKey = paywallBanner
    ? unlimitedOnlyActive
      ? "CREATOR_PLAT"
      : paywallBanner.requiredTierKey
    : null;

  // Default paywalls to Annual (ALL-1512), EXCEPT surfaces that can offer the
  // 7-day free trial — those stay Monthly so the trial CTA remains visible (it's
  // gated on !isAnnual). Callers can still override via initialBillingPeriod.
  const [billingPeriod, setBillingPeriod] = useState<EPriceTabView>(
    initialBillingPeriod ??
      (trialOfferable ? EPriceTabView.Monthly : EPriceTabView.Annual),
  );
  // The caller preselects its requiredTierKey (e.g. ESSENTIALS); when the
  // experiment hides that card, seed the selection with the one card the
  // grid will show, or checkout would buy a plan the grid never offered.
  const [selectedPlan, setSelectedPlan] = useState<string | null>(() =>
    unlimitedOnlyActive && initialPlan && initialPlan !== "CREATOR_PLAT"
      ? "CREATOR_PLAT"
      : initialPlan ?? null,
  );
  const [selectedAddon, setSelectedAddon] = useState<IStorageAddOn | null>(
    null,
  );

  const isAnnual = billingPeriod === EPriceTabView.Annual;
  const isTrialEligible = trialOfferable && !isAnnual;

  const availableUpgradeTiers = unlimitedOnlyActive
    ? (["CREATOR_PLAT"] as const)
    : currentTierIndex === -1
    ? UPGRADE_TIERS // FREE → show all
    : tierOrder.filter((_, i) => i > currentTierIndex);

  const isUnlimitedSelected = selectedPlan === "CREATOR_PLAT";

  const accentColor = selectedPlan
    ? getTierAccentColor(selectedPlan)
    : getTierAccentColor(isFreeUser ? "ESSENTIALS" : currentTier);

  // Compute total storage display
  const currentTotalLimit =
    storageStatus?.limit ?? TIER_STORAGE_NUM[currentTier];
  const planClips = selectedPlan ? TIER_STORAGE_NUM[selectedPlan] ?? 0 : 0;
  const addonClips = selectedAddon?.clips ?? 0;
  // When a plan is selected it replaces the current base storage,
  // so we use planClips as the base. When no plan is selected the
  // user keeps their current limit and only adds addon clips on top.
  const baseClips = selectedPlan ? planClips : currentTotalLimit;
  const displayTotal = baseClips !== null ? baseClips + addonClips : null;

  const paywallAnalytics = paywallBanner
    ? {
        paywallFeature: paywallBanner.featureName,
        paywallRequiredTier: paywallBanner.requiredTierKey,
      }
    : {};

  // Auto-clear addon when switching to Unlimited
  useEffect(() => {
    if (isUnlimitedSelected && selectedAddon) {
      setSelectedAddon(null);
    }
  }, [isUnlimitedSelected, selectedAddon]);

  // Xsolla doesn't support addons — clear any selection if provider resolves to xsolla
  useEffect(() => {
    if (isXsolla && selectedAddon) {
      setSelectedAddon(null);
    }
  }, [isXsolla, selectedAddon]);

  useEffect(() => {
    analytics.track("Upgrade Modal - Viewed", {
      source: analyticsSource,
      currentTier,
      paywall_variant: paywallVariant,
      ...paywallAnalytics,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDismiss = () => {
    analytics.track("Upgrade Modal - Dismissed", {
      source: analyticsSource,
      currentTier,
      paywall_variant: paywallVariant,
      ...paywallAnalytics,
    });
    setPaystation((prev) => ({
      ...prev,
      additionalLookups: undefined,
    }));
    onClose();
  };

  const handlePlanSelect = (tier: string) => {
    setSelectedPlan(tier === selectedPlan ? null : tier);
    analytics.track("Upgrade Modal - Plan Selected", {
      source: analyticsSource,
      selectedPlan: tier,
      billingPeriod,
      currentTier,
      paywall_variant: paywallVariant,
      ...paywallAnalytics,
    });
  };

  const handleAddonToggle = (addon: IStorageAddOn) => {
    // If Unlimited is selected, addons are included — do nothing
    // FREE users must select a plan before they can add storage
    if (isUnlimitedSelected || (isFreeUser && !selectedPlan)) return;
    const isSelected = selectedAddon?.lookupKey === addon.lookupKey;
    setSelectedAddon(isSelected ? null : addon);
    if (!isSelected) {
      analytics.track("Upgrade Modal - Storage Selected", {
        source: analyticsSource,
        lookupKey: addon.lookupKey,
        clips: addon.clips,
        price: isAnnual ? addon.annualPrice : addon.price,
        billingPeriod,
        currentTier,
        paywall_variant: paywallVariant,
        ...paywallAnalytics,
      });
    }
  };

  const handleCheckout = async () => {
    const resolvedProvider = await resolveProvider();
    const useXsolla = resolvedProvider === "xsolla";

    let lookUp: string | undefined;
    const additionalLookups: string[] = [];

    if (selectedPlan) {
      const table = useXsolla ? TIER_XSOLLA_LOOKUP : TIER_STRIPE_LOOKUP;
      lookUp = table[selectedPlan]?.[billingPeriod];
      if (!useXsolla && selectedAddon) {
        additionalLookups.push(
          isAnnual ? selectedAddon.annualLookupKey : selectedAddon.lookupKey,
        );
      }
    } else if (!useXsolla && selectedAddon) {
      lookUp = isAnnual
        ? selectedAddon.annualLookupKey
        : selectedAddon.lookupKey;
    }

    if (!lookUp) return;

    const isTrial = isUnlimitedSelected && isTrialEligible;
    // Trial checkouts skip the coupon param entirely (Checkout/index.tsx sends
    // trial XOR coupon), and the discount is for a paid plan, not an addon-only cart.
    const applyFaceitDiscount =
      !useXsolla && !isTrial && !!selectedPlan && hasFaceitPremiumDiscount;

    analytics.track("Upgrade Modal - Checkout Initiated", {
      source: analyticsSource,
      paywall_variant: paywallVariant,
      selectedPlan: selectedPlan ?? currentTier,
      selectedAddon: useXsolla ? null : selectedAddon?.lookupKey ?? null,
      billingPeriod,
      currentTier,
      totalItems: useXsolla ? 1 : 1 + additionalLookups.length,
      isTrial,
      provider: resolvedProvider,
      faceitPremiumDiscountApplied: applyFaceitDiscount,
      ...paywallAnalytics,
    });

    if (useXsolla) {
      setXsollaPaystation((prev) => ({
        ...prev,
        isModalOpen: true,
        type: "subscribe",
        lookUp,
        trial: isTrial,
      }));
    } else {
      setPaystation((prev) => ({
        ...prev,
        isModalOpen: true,
        type: "subscribe",
        lookUp,
        trial: isTrial,
        ...(additionalLookups.length ? { additionalLookups } : {}),
        ...(applyFaceitDiscount ? { couponId: FACEIT_PREMIUM_COUPON } : {}),
      }));
    }

    onClose();
  };

  // FREE users must select a plan; paid users can checkout addon-only.
  // Xsolla users must select a plan (no addon path exists).
  const canCheckout = isXsolla
    ? !!selectedPlan
    : isFreeUser
    ? !!selectedPlan
    : !!selectedPlan || !!selectedAddon;

  const isTrialCheckout = isUnlimitedSelected && isTrialEligible;

  const checkoutLabel = isTrialCheckout
    ? "Start Free Trial"
    : !isXsolla && selectedPlan && selectedAddon
    ? "Upgrade & Add Storage"
    : selectedPlan
    ? "Upgrade Plan"
    : "Add Storage";

  const handleDiscordBotCtaClick = () => {
    analytics.track("Clip Limit Discord Bot CTA Click", {
      source: analyticsSource,
      currentTier,
      // Hiding the cheaper tiers may push people to the free CTAs instead;
      // tag these so the readout can see it.
      paywall_variant: paywallVariant,
      ...paywallAnalytics,
    });
    // Navigating to the bot page unmounts the modal; close it explicitly too.
    onClose();
  };

  const handleAllstarCtaClick = () => {
    analytics.track("Clip Limit Allstar CTA Click", {
      source: analyticsSource,
      currentTier,
      paywall_variant: paywallVariant,
      ...paywallAnalytics,
    });
    onClose();
  };

  return (
    <ModalWrapper
      $borderColor={getTierBorderColor(selectedPlan ?? currentTier)}
      $glowColor={getTierGlowColor(selectedPlan ?? currentTier)}
    >
      <CardHeader
        $gradient={getTierHeaderGradient(selectedPlan ?? currentTier)}
      >
        {selectedPlan && (
          <BackgroundBadgeWrapper>
            {getTierIcon(selectedPlan)}
          </BackgroundBadgeWrapper>
        )}
        <BadgeRow>
          <StorageBadge $color={accentColor}>
            <HiArchive size={10} />
            Upgrade & Storage
          </StorageBadge>
        </BadgeRow>
        <Title>
          {paywallBanner
            ? `Upgrade to ${paywallBanner.upgradeAction}`
            : "Upgrade & Expand Storage"}
        </Title>
        <Subtitle>
          {paywallBanner
            ? "Choose a plan to unlock this feature"
            : isFreeUser
            ? "Choose a plan and add extra clip storage"
            : "Upgrade your plan and add extra clip storage"}
        </Subtitle>
        {paywallBanner && bannerTierKey && (
          <PaywallBannerBar $accentColor={getTierAccentColor(bannerTierKey)}>
            <PaywallBannerLock />
            <PaywallBannerText>
              <PaywallBannerFeature>
                {paywallBanner.featureName}
              </PaywallBannerFeature>
              {" — requires "}
              <PaywallBannerTier $color={getTierAccentColor(bannerTierKey)}>
                {TIER_NAMES[bannerTierKey]}
              </PaywallBannerTier>
              {bannerTierKey !== "CREATOR_PLAT" && " or higher"}
            </PaywallBannerText>
          </PaywallBannerBar>
        )}
        {paywallBanner?.description && (
          <PaywallDescription>{paywallBanner.description}</PaywallDescription>
        )}
      </CardHeader>

      <ModalContent>
        {showDiscordBotCta && (
          <FreeCtaSection>
            <FreeCtaHeading>Tired of clip limits?</FreeCtaHeading>
            <FreeCtaStack>
              {isCs2Clip && (
                <FreeCta to={ALLSTAR_ROUTE} onClick={handleAllstarCtaClick}>
                  <FreeCtaIcon $accent={ALLSTAR_ACCENT}>
                    <FaCommentDots size={18} />
                  </FreeCtaIcon>
                  <FreeCtaText>
                    <FreeCtaTitle>Skip clip limits with !allstar</FreeCtaTitle>
                    <FreeCtaSubtitle>
                      Type !allstar in all chat after a play and we'll clip it
                      free, even if you've hit your limit
                    </FreeCtaSubtitle>
                  </FreeCtaText>
                  <FreeCtaArrow $accent={ALLSTAR_ACCENT}>→</FreeCtaArrow>
                </FreeCta>
              )}
              <FreeCta
                to={ALLSTAR_BOT_ROUTE}
                onClick={handleDiscordBotCtaClick}
              >
                <FreeCtaIcon $accent={DISCORD_ACCENT}>
                  <FaDiscord size={20} />
                </FreeCtaIcon>
                <FreeCtaText>
                  <FreeCtaTitle>Get more clips for free</FreeCtaTitle>
                  <FreeCtaSubtitle>
                    Add the Allstar bot to your Discord server to raise your
                    clip limit
                  </FreeCtaSubtitle>
                </FreeCtaText>
                <FreeCtaArrow $accent={DISCORD_ACCENT}>→</FreeCtaArrow>
              </FreeCta>
            </FreeCtaStack>
          </FreeCtaSection>
        )}
        {!isFreeUser && currentTotalLimit !== null && storageStatus && (
          <CurrentStorageBar>
            <CurrentStorageInfo>
              <CurrentStorageLabel>Your storage</CurrentStorageLabel>
              <CurrentStorageCount>
                <CurrentStorageUsed
                  $isOver={storageStatus.savedCount > currentTotalLimit}
                >
                  {storageStatus.savedCount}
                </CurrentStorageUsed>
                {" / "}
                {currentTotalLimit} clips
              </CurrentStorageCount>
            </CurrentStorageInfo>
            <CurrentProgressBar>
              <CurrentProgressFill
                $percentage={Math.min(
                  (storageStatus.savedCount / currentTotalLimit) * 100,
                  100,
                )}
                $isOver={storageStatus.savedCount > currentTotalLimit}
              />
            </CurrentProgressBar>
          </CurrentStorageBar>
        )}

        <ToggleContainer>
          <ToggleOption
            $active={!isAnnual}
            $accentColor={accentColor}
            onClick={() => setBillingPeriod(EPriceTabView.Monthly)}
          >
            Monthly
          </ToggleOption>
          <ToggleOption
            $active={isAnnual}
            $accentColor={accentColor}
            onClick={() => setBillingPeriod(EPriceTabView.Annual)}
          >
            Annual
          </ToggleOption>
          {!isAnnual && (
            <FloatingSaveBadge
              $accentColor={accentColor}
              onClick={() => setBillingPeriod(EPriceTabView.Annual)}
            >
              Save up to 50%
            </FloatingSaveBadge>
          )}
        </ToggleContainer>

        <Section>
          <SectionLabel>
            {isFreeUser ? "Choose a Plan" : "Upgrade Your Plan (Optional)"}
          </SectionLabel>
          {hasFaceitPremiumDiscount && (
            <FaceitPlanBanner>
              <FaceitPlanBannerIcon />
              <FaceitPlanBannerText>
                FACEIT Premium members save an extra{" "}
                {FACEIT_PREMIUM_DISCOUNT_PERCENT}% — applied at checkout
              </FaceitPlanBannerText>
            </FaceitPlanBanner>
          )}
          <PlanGrid>
            {availableUpgradeTiers.map((tier) => {
              const pricing = TIER_PRICING[tier];
              const isSelected = selectedPlan === tier;
              const tierAccent = getTierAccentColor(tier);
              // Honor a live promo's per-tier sale price/discount when present.
              const promoKey = TIER_PROMO_KEY[tier];
              const period = isAnnual ? "annual" : "monthly";
              const promoPrice = promoKey
                ? activePromo?.pricing?.[period]?.[promoKey]
                : "";
              const promoPcntOff = promoKey
                ? activePromo?.pcntOff?.[period]?.[promoKey]
                : "";
              return (
                <PlanOption
                  key={tier}
                  $selected={isSelected}
                  $accentColor={tierAccent}
                  onClick={() => handlePlanSelect(tier)}
                >
                  {tier === "CREATOR_PLAT" && isTrialEligible ? (
                    <TrialBadge $color={tierAccent}>Free Trial</TrialBadge>
                  ) : promoPcntOff ? (
                    <DiscountBadge $color={tierAccent}>
                      {promoPcntOff}
                    </DiscountBadge>
                  ) : isAnnual && pricing.discount ? (
                    <DiscountBadge $color={tierAccent}>
                      Save {pricing.discount}
                    </DiscountBadge>
                  ) : null}
                  <PlanIconWrapper>{getTierIcon(tier)}</PlanIconWrapper>
                  <PlanName>{TIER_NAMES[tier]}</PlanName>
                  {tier === "CREATOR_PLAT" && isTrialEligible ? (
                    <PlanPriceRow>
                      <PlanPrice>Free</PlanPrice>
                      <PlanPeriod>
                        {trialRequiresCard
                          ? "7 days"
                          : "7 days · no card required"}
                      </PlanPeriod>
                    </PlanPriceRow>
                  ) : (
                    <PlanPriceRow>
                      <PlanPrice>
                        {promoPrice
                          ? `$${promoPrice}`
                          : isAnnual
                          ? pricing.annual
                          : pricing.monthly}
                      </PlanPrice>
                      <PlanPeriod>/mo</PlanPeriod>
                    </PlanPriceRow>
                  )}
                  <PlanStorage>{TIER_STORAGE[tier]} storage</PlanStorage>
                  {isSelected && (
                    <SelectedCheck $color={tierAccent}>
                      <HiCheck size={14} />
                    </SelectedCheck>
                  )}
                </PlanOption>
              );
            })}
          </PlanGrid>
        </Section>

        {/* Upsell banner — hidden for Xsolla users (addons not supported) */}
        {!isXsolla && (
          <UpsellBanner $accentColor={accentColor}>
            <UpsellLeft>
              <UpsellIconWrapper $accentColor={accentColor}>
                <HiFilm className="clip clip-back" />
                <HiFilm className="clip clip-front" />
              </UpsellIconWrapper>
              <UpsellLabel>Add Extra Storage</UpsellLabel>
            </UpsellLeft>
            <UpsellRight>
              {isUnlimitedSelected ? (
                <IncludedBadge $accentColor={accentColor}>
                  <HiCheck size={14} />
                  Included
                </IncludedBadge>
              ) : isFreeUser && !selectedPlan ? (
                <UpsellHint>Select a plan to add storage</UpsellHint>
              ) : (
                <PillRow>
                  {STORAGE_ADDON_OPTIONS.map((addon) => {
                    const isSelected =
                      selectedAddon?.lookupKey === addon.lookupKey;
                    return (
                      <AddonPill
                        key={addon.lookupKey}
                        $selected={isSelected}
                        $accentColor={accentColor}
                        onClick={() => handleAddonToggle(addon)}
                      >
                        <ClipPileIcon
                          $size={addon.clips <= 50 ? "small" : "large"}
                          $accentColor={accentColor}
                        >
                          {addon.clips > 50 && (
                            <>
                              <HiFilm className="clip clip-far-back" />
                              <HiFilm className="clip clip-back" />
                            </>
                          )}
                          <HiFilm className="clip clip-mid" />
                          <HiFilm className="clip clip-front" />
                        </ClipPileIcon>
                        <PillClips>+{addon.clips}</PillClips>
                        <PillPrice>
                          {isAnnual ? addon.annualPrice : addon.price}/mo
                        </PillPrice>
                        {isAnnual && (
                          <PillDiscountBadge $color={accentColor}>
                            Save {addon.annualDiscount}
                          </PillDiscountBadge>
                        )}
                      </AddonPill>
                    );
                  })}
                </PillRow>
              )}
            </UpsellRight>
          </UpsellBanner>
        )}

        {/* Bottom bar: summary + checkout */}
        <CheckoutBar>
          <CheckoutSummary>
            <SummaryIcon $accentColor={accentColor}>
              <HiArchive size={16} />
            </SummaryIcon>
            <SummaryText>
              {isUnlimitedSelected ? (
                <SummaryTotal $accentColor={accentColor}>
                  Unlimited clips
                </SummaryTotal>
              ) : displayTotal !== null ? (
                <>
                  {selectedPlan || addonClips > 0 ? (
                    <SummaryValue>
                      {selectedPlan ? (
                        <SummaryNumber $highlight $accentColor={accentColor}>
                          {planClips}
                        </SummaryNumber>
                      ) : (
                        <SummaryNumber>{currentTotalLimit}</SummaryNumber>
                      )}
                      {addonClips > 0 && (
                        <>
                          <SummaryOperator>+</SummaryOperator>
                          <SummaryNumber $highlight $accentColor={accentColor}>
                            {addonClips}
                          </SummaryNumber>
                        </>
                      )}
                      <SummaryEquals>=</SummaryEquals>
                      <SummaryTotal $accentColor={accentColor}>
                        {displayTotal} clips
                      </SummaryTotal>
                    </SummaryValue>
                  ) : (
                    <SummaryTotal $accentColor={accentColor}>
                      {currentTotalLimit} clips
                    </SummaryTotal>
                  )}
                </>
              ) : null}
            </SummaryText>
          </CheckoutSummary>
          <CheckoutButton
            $gradient={getTierButtonGradient(selectedPlan ?? currentTier)}
            onClick={handleCheckout}
            disabled={!canCheckout}
            $disabled={!canCheckout}
          >
            {checkoutLabel}
          </CheckoutButton>
        </CheckoutBar>
      </ModalContent>
    </ModalWrapper>
  );
};

// ── Styled Components ──────────────────────────────────────────────

const ModalWrapper = styled(Box)<{
  $borderColor: string;
  $glowColor: string;
}>`
  background: ${({ theme }) => theme.colors.ned};
  border: 1px solid ${({ $borderColor }) => $borderColor};
  border-radius: 16px;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.25),
    0 2px 4px rgba(0, 0, 0, 0.15),
    0 0 24px ${({ $glowColor }) => $glowColor};
  overflow: hidden;
  width: min(640px, 90vw);
`;

const CardHeader = styled(Box)<{ $gradient: string }>`
  background: ${({ $gradient }) => $gradient};
  border-radius: 16px 16px 0 0;
  overflow: hidden;
  padding: 32px 32px 24px;
  position: relative;
`;

const BackgroundBadgeWrapper = styled(Box)`
  left: -45px;
  opacity: 0.15;
  position: absolute;
  top: -55px;
  z-index: 0;

  svg {
    height: 140px !important;
    -webkit-mask: linear-gradient(
      to right,
      black 0%,
      black 40%,
      transparent 85%
    );
    mask: linear-gradient(to right, black 0%, black 40%, transparent 85%);
    -webkit-mask-size: 100% 100%;
    mask-size: 100% 100%;
    position: relative;
    width: 140px !important;
    z-index: 1;
  }
`;

const BadgeRow = styled(Flex)`
  position: relative;
  z-index: 1;
`;

const StorageBadge = styled(Flex)<{ $color: string }>`
  align-items: center;
  background: ${({ $color }) => `${$color}1a`};
  border: 1px solid ${({ $color }) => `${$color}55`};
  border-radius: 4px;
  color: ${({ $color }) => $color};
  font-size: 10px;
  font-weight: 700;
  gap: 4px;
  letter-spacing: 1.5px;
  margin-bottom: 10px;
  padding: 3px 8px;
  text-transform: uppercase;
`;

const Title = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 24px;
  font-weight: 700;
  line-height: 1.3;
  position: relative;
  z-index: 1;
`;

const Subtitle = styled(Text)`
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  font-weight: 500;
  margin-top: 4px;
  position: relative;
  z-index: 1;
`;

const PaywallBannerBar = styled(Flex)<{ $accentColor: string }>`
  align-items: center;
  background: rgba(0, 0, 0, 0.25);
  border-left: 3px solid ${({ $accentColor }) => $accentColor};
  border-radius: 6px;
  gap: 8px;
  margin-top: 16px;
  padding: 10px 14px;
  position: relative;
  z-index: 1;
`;

const PaywallBannerLock = styled(IoLockClosed)`
  color: rgba(255, 255, 255, 0.5);
  flex-shrink: 0;
  height: 14px;
  width: 14px;
`;

const PaywallBannerText = styled(Text)`
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
`;

const PaywallBannerFeature = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-weight: 700;
`;

const PaywallBannerTier = styled.span<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-weight: 700;
`;

const PaywallDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  margin: 10px 0 0;
  position: relative;
  z-index: 1;
`;

const ModalContent = styled(Flex)`
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  flex-direction: column;
  gap: 24px;
  padding: 24px 32px 32px;
`;

// Free-clip CTA cards shown on the clip-limit paywall
const FreeCtaSection = styled(Box)`
  display: flex;
  flex-direction: column;
`;

const FreeCtaHeading = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 16px;
  font-weight: 800;
  margin-bottom: 16px;
`;

const FreeCtaStack = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FreeCta = styled(Link)`
  align-items: center;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  text-decoration: none;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;

  & > span {
    transition: transform 0.15s ease;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.16);
  }

  &:hover > span {
    transform: translateX(3px);
  }
`;

const FreeCtaIcon = styled(Flex)<{ $accent: string }>`
  align-items: center;
  background: rgba(${({ $accent }) => $accent}, 0.2);
  border-radius: 8px;
  color: rgb(${({ $accent }) => $accent});
  flex-shrink: 0;
  height: 36px;
  justify-content: center;
  width: 36px;
`;

const FreeCtaText = styled(Box)`
  flex: 1;
  min-width: 0;
`;

const FreeCtaTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 700;
`;

const FreeCtaSubtitle = styled(Text)`
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  font-weight: 500;
  margin-top: 2px;
`;

const FreeCtaArrow = styled.span<{ $accent: string }>`
  color: rgb(${({ $accent }) => $accent});
  flex-shrink: 0;
  font-size: 18px;
  font-weight: 700;
`;

const CurrentStorageBar = styled(Box)`
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 14px 16px;
`;

const CurrentStorageInfo = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const CurrentStorageLabel = styled(Text)`
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CurrentStorageCount = styled(Text)`
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  font-weight: 600;
`;

const CurrentStorageUsed = styled.span<{ $isOver: boolean }>`
  color: ${({ $isOver, theme }) =>
    $isOver ? theme.colors.alert : theme.colors.envy};
  font-weight: 700;
`;

const CurrentProgressBar = styled(Box)`
  background: rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  height: 6px;
  overflow: hidden;
  width: 100%;
`;

const CurrentProgressFill = styled(Box)<{
  $percentage: number;
  $isOver: boolean;
}>`
  background: ${({ $isOver, theme, $percentage }) =>
    $isOver
      ? theme.colors.alert
      : $percentage >= 80
      ? theme.colors.caution
      : theme.colors.envy};
  border-radius: 4px;
  height: 100%;
  transition: width 0.3s ease;
  width: ${({ $percentage }) => Math.min($percentage, 100)}%;
`;

const ToggleContainer = styled(Flex)`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  padding: 4px;
  position: relative;
`;

const ToggleOption = styled.button<{
  $active: boolean;
  $accentColor: string;
}>`
  background: ${({ $active, theme }) =>
    $active ? theme.colors.ned : "transparent"};
  border: 1px solid
    ${({ $active }) => ($active ? "rgba(255, 255, 255, 0.1)" : "transparent")};
  border-radius: 6px;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.ice : theme.colors.chalk};
  align-items: center;
  cursor: pointer;
  display: flex;
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  gap: 6px;
  justify-content: center;
  padding: 8px 0;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ $active, theme }) =>
      $active ? theme.colors.ned : theme.colors.darkerNed};
    color: ${({ $active, theme }) =>
      $active ? theme.colors.ice : theme.colors.iceAlpha60};
  }
`;

const floatPulse = `
  @keyframes saveBadgePulse {
    0%, 100% { transform: translateX(-50%) rotate(-2deg) scale(1); }
    50% { transform: translateX(-50%) rotate(-2deg) scale(1.05); }
  }
`;

const FloatingSaveBadge = styled.button<{ $accentColor: string }>`
  ${floatPulse}
  animation: saveBadgePulse 2s ease-in-out infinite;
  background: ${({ $accentColor }) => $accentColor};
  border: none;
  border-radius: 4px;
  box-shadow: 0 2px 8px ${({ $accentColor }) => `${$accentColor}50`};
  color: white;
  cursor: pointer;
  font-size: 10px;
  font-weight: 700;
  left: 75%;
  letter-spacing: 0.5px;
  padding: 3px 8px;
  position: absolute;
  text-transform: uppercase;
  top: -10px;
  transform: translateX(-50%) rotate(-2deg);
  white-space: nowrap;
  z-index: 2;
`;

const Section = styled(Box)``;

const SectionLabel = styled(Text)`
  color: rgba(255, 255, 255, 0.4);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.2px;
  margin-bottom: 10px;
  text-transform: uppercase;
`;

const PlanGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
`;

const FaceitPlanBanner = styled(Flex)`
  align-items: center;
  background: #000000;
  border: 1px solid ${({ theme }) => theme.colors.faceit};
  border-radius: 8px;
  gap: 8px;
  margin-bottom: 12px;
  padding: 10px 14px;
  width: 100%;
`;

const FaceitPlanBannerIcon = styled(SiFaceit)`
  color: ${({ theme }) => theme.colors.faceit};
  flex-shrink: 0;
  height: 18px;
  width: 18px;
`;

const FaceitPlanBannerText = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 13px;
  font-weight: 600;
`;

const PlanOption = styled(Box)<{
  $selected: boolean;
  $accentColor: string;
}>`
  background: ${({ $selected, $accentColor }) =>
    $selected
      ? `linear-gradient(135deg, ${$accentColor}18 0%, ${$accentColor}08 100%)`
      : `linear-gradient(135deg, ${$accentColor}0a 0%, transparent 100%)`};
  border: 2px solid
    ${({ $selected, $accentColor }) =>
      $selected ? `${$accentColor}88` : `${$accentColor}30`};
  border-radius: 12px;
  box-shadow: ${({ $selected, $accentColor }) =>
    $selected
      ? `0 0 16px ${$accentColor}25, inset 0 1px 0 ${$accentColor}15`
      : `inset 0 1px 0 rgba(255, 255, 255, 0.03)`};
  cursor: pointer;
  padding: 18px 14px;
  position: relative;
  text-align: center;
  transition: all 0.15s ease;

  &:hover {
    background: linear-gradient(
      135deg,
      ${({ $accentColor }) => $accentColor}15 0%,
      ${({ $accentColor }) => $accentColor}08 100%
    );
    border-color: ${({ $accentColor }) => `${$accentColor}66`};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const PlanIconWrapper = styled(Box)`
  margin-bottom: 8px;

  svg {
    height: 28px !important;
    width: 28px !important;
  }
`;

const PlanName = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 700;
`;

const PlanPriceRow = styled(Flex)`
  align-items: baseline;
  gap: 3px;
  justify-content: center;
  margin-top: 6px;
`;

const PlanPrice = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
`;

const PlanPeriod = styled.span`
  color: rgba(255, 255, 255, 0.35);
  font-size: 13px;
  font-weight: 500;
`;

const PlanStorage = styled(Text)`
  color: rgba(255, 255, 255, 0.35);
  font-size: 12px;
  font-weight: 500;
  margin-top: 6px;
`;

const DiscountBadge = styled.span<{ $color: string }>`
  background: ${({ $color }) => `${$color}20`};
  border: 1px solid ${({ $color }) => `${$color}40`};
  border-radius: 4px;
  color: ${({ $color }) => $color};
  font-size: 10px;
  font-weight: 700;
  left: 8px;
  letter-spacing: 0.5px;
  padding: 2px 6px;
  position: absolute;
  top: 8px;
  text-transform: uppercase;
`;

const TrialBadge = styled.span<{ $color: string }>`
  background: ${({ $color }) => $color};
  border-radius: 4px;
  color: white;
  font-size: 10px;
  font-weight: 700;
  left: 8px;
  letter-spacing: 0.5px;
  padding: 2px 6px;
  position: absolute;
  top: 8px;
  text-transform: uppercase;
`;

const SelectedCheck = styled(Flex)<{ $color: string }>`
  align-items: center;
  background: ${({ $color }) => $color};
  border-radius: 50%;
  color: white;
  height: 20px;
  justify-content: center;
  position: absolute;
  right: 6px;
  top: 6px;
  width: 20px;
`;

// ── Upsell Banner ──────────────────────────────────────────────────

const UpsellBanner = styled(Flex)<{ $accentColor: string }>`
  align-items: center;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  gap: 16px;
  justify-content: space-between;
  padding: 16px 20px;
`;

const UpsellLeft = styled(Flex)`
  align-items: center;
  flex-shrink: 0;
  gap: 12px;
`;

const UpsellIconWrapper = styled.div<{ $accentColor: string }>`
  height: 32px;
  position: relative;
  width: 32px;

  .clip {
    color: ${({ $accentColor }) => $accentColor};
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
    position: absolute;
  }

  .clip-back {
    font-size: 18px;
    left: 0;
    opacity: 0.5;
    top: 2px;
    transform: rotate(-10deg);
  }

  .clip-front {
    font-size: 22px;
    opacity: 1;
    right: 0;
    top: 4px;
    transform: rotate(8deg);
  }
`;

const UpsellLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
`;

const UpsellRight = styled(Flex)`
  align-items: center;
`;

const UpsellHint = styled(Text)`
  color: rgba(255, 255, 255, 0.35);
  font-size: 12px;
  font-style: italic;
  font-weight: 500;
`;

const IncludedBadge = styled(Flex)<{ $accentColor: string }>`
  align-items: center;
  color: ${({ $accentColor }) => $accentColor};
  font-size: 13px;
  font-weight: 600;
  gap: 4px;
`;

const PillRow = styled(Flex)`
  gap: 8px;
`;

const AddonPill = styled.button<{
  $selected: boolean;
  $accentColor: string;
}>`
  background: ${({ $selected, $accentColor }) =>
    $selected ? `${$accentColor}20` : "transparent"};
  border: 1.5px solid
    ${({ $selected, $accentColor }) =>
      $selected ? `${$accentColor}88` : "rgba(255, 255, 255, 0.15)"};
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 10px 18px;
  position: relative;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ $selected, $accentColor }) =>
      $selected ? `${$accentColor}20` : "rgba(255, 255, 255, 0.06)"};
    border-color: ${({ $accentColor }) => `${$accentColor}66`};
  }
`;

const PillClips = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
`;

const PillPrice = styled(Text)`
  color: rgba(255, 255, 255, 0.45);
  font-size: 11px;
  font-weight: 500;
`;

const PillDiscountBadge = styled(Text)<{ $color: string }>`
  background: ${({ $color }) => `${$color}20`};
  border: 1px solid ${({ $color }) => `${$color}40`};
  border-radius: 4px;
  color: ${({ $color }) => $color};
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-top: 2px;
  padding: 1px 6px;
  text-transform: uppercase;
`;

const ClipPileIcon = styled.div<{
  $size: "small" | "large";
  $accentColor: string;
}>`
  height: 32px;
  margin-bottom: 4px;
  position: relative;
  width: ${({ $size }) => ($size === "large" ? "56px" : "36px")};

  .clip {
    color: ${({ $accentColor }) => $accentColor};
    filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.4));
    position: absolute;
    transition: transform 0.15s ease;
  }

  /* Small pile: 2 icons */
  .clip-mid {
    font-size: ${({ $size }) => ($size === "large" ? "20px" : "20px")};
    left: ${({ $size }) => ($size === "large" ? "16px" : "0")};
    opacity: ${({ $size }) => ($size === "large" ? 0.6 : 0.5)};
    top: ${({ $size }) => ($size === "large" ? "2px" : "0")};
    transform: rotate(-8deg);
  }

  .clip-front {
    font-size: ${({ $size }) => ($size === "large" ? "24px" : "22px")};
    opacity: 1;
    right: 0;
    top: ${({ $size }) => ($size === "large" ? "4px" : "4px")};
    transform: rotate(8deg);
  }

  /* Large pile only: extra back icons for a fuller stack */
  .clip-far-back {
    font-size: 16px;
    left: 0;
    opacity: 0.25;
    top: 0;
    transform: rotate(-20deg);
  }

  .clip-back {
    font-size: 18px;
    left: 8px;
    opacity: 0.4;
    top: 1px;
    transform: rotate(-14deg);
  }
`;

// ── Checkout Bar ───────────────────────────────────────────────────

const CheckoutBar = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => `${theme.colors.ned}`};
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  gap: 16px;
  justify-content: space-between;
  margin: 0 -32px -32px;
  padding: 20px 32px;
`;

const CheckoutSummary = styled(Flex)`
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const SummaryIcon = styled(Flex)<{ $accentColor: string }>`
  align-items: center;
  background: ${({ $accentColor }) => `${$accentColor}1a`};
  border-radius: 8px;
  color: ${({ $accentColor }) => $accentColor};
  flex-shrink: 0;
  height: 32px;
  justify-content: center;
  width: 32px;
`;

const SummaryText = styled(Box)`
  min-width: 0;
`;

const SummaryValue = styled(Flex)`
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px;
`;

const SummaryNumber = styled(Text)<{
  $highlight?: boolean;
  $accentColor?: string;
}>`
  color: ${({ $highlight, $accentColor, theme }) =>
    $highlight && $accentColor ? $accentColor : theme.colors.ice};
  font-size: 15px;
  font-weight: 700;
`;

const SummaryOperator = styled(Text)`
  color: rgba(255, 255, 255, 0.3);
  font-size: 13px;
  font-weight: 500;
`;

const SummaryEquals = styled(Text)`
  color: rgba(255, 255, 255, 0.3);
  font-size: 13px;
  font-weight: 500;
`;

const SummaryTotal = styled(Text)<{ $accentColor: string }>`
  color: ${({ $accentColor }) => $accentColor};
  font-size: 16px;
  font-weight: 700;
`;

const CheckoutButton = styled.button<{
  $gradient: string;
  $disabled: boolean;
}>`
  background: ${({ $gradient }) => $gradient};
  border: none;
  border-radius: 8px;
  color: white;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 600;
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  padding: 10px 24px;
  transition: filter 0.2s ease;
  white-space: nowrap;

  &:hover {
    filter: ${({ $disabled }) => ($disabled ? "none" : "brightness(1.1)")};
  }

  &:active {
    filter: ${({ $disabled }) => ($disabled ? "none" : "brightness(0.95)")};
  }
`;

export { StorageAddonModal };
export type { PaywallBannerData };
